import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ComponentFactoryService } from '../../services/component-factory.service';
import { Actions, ofType } from '@ngrx/effects';
import { MessagesActions } from '../../state/messages/messages.actions';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { WindowStateActions } from '../../state/window/window.actions';
import { selectMessagesState } from '../../state/messages/messages.reducer';
import { OverlayActions } from '../../state/overlay-state/overlay.actions';
import { selectOverlayState } from '../../state/overlay-state/overlay.reducer';
import { map, Observable, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  FooterMessageComponent,
  HeaderComponent,
  MessageTypingIndicatorComponent,
} from '@ucx-js/chat-ui';
import { MainComponentActions } from '../../state/main.actions';
import { TypingActions } from '../../state/typing/typing.actions';
import { selectUserInputState } from '../../state/user-input/user-input.reducer';
import { selectIsVaOrAgentTyping } from '../../state/typing/typing.reducer';
import {
  selectIsLiveChatHeader,
  selectLogo,
  selectTitle,
} from '../../state/chat-header/chat-header.reducer';
import { AddressAutocompleteAction } from '../../state/address-autocomplete/address-autocomplete.action';
import { selectAddressList } from '../../state/address-autocomplete/address-autocomplete.reducer';
import { ChatAdminActions } from '../../state/chat-admin/chat-admin.actions';
import { appConfig } from '../../../init/getAppConfig';
import { selectSettingsState } from '../../state/settings/settings.reducers';
import { SettingsActions } from '../../state/settings/settings.action';
import { getFontSize } from '../../../utils/getFontSize';
import { OverlayType, RealmName } from '../../types/enum';
import { getBrand } from '../../../utils/getBrand';
import { AuthTokenActions } from '../../state/auth-token/auth-token.actions';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    MessageTypingIndicatorComponent,
    FooterMessageComponent,
    TranslateModule,
  ],
  selector: 'ucx-js-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messageContainer') messageContainer: ElementRef | undefined;
  @ViewChild('messageContainerRef', { read: ViewContainerRef })
  messageContainerRef: ViewContainerRef | undefined;
  @ViewChild('overlayComponentRef', { read: ViewContainerRef })
  overlayComponentRef: ViewContainerRef | undefined;
  @ViewChild('alertBannerRef', { read: ViewContainerRef })
  alertBannerRef: ViewContainerRef | undefined;
  @ViewChild('footerContainer') footerContainer: ElementRef | undefined;
  menuItems: Observable<any> = this.store.select(selectSettingsState).pipe(
    map((settings) => {
      return [
        {
          menuItemText: this.translateService.instant(
            'SETTINGS.NOTIFICATION_TITLE'
          ),
          menuItemLabel: this.translateService.instant('NOTIFICATION_LABEL'),
          menuItemKey: 'NOTIFICATIONS',
          menuItemType: 'switch',
          menuItemOptions: [
            { label: 'ON', value: true },
            { label: 'OFF', value: false },
          ],
          selectedMenuItemOption: settings?.notifications,
        },
        {
          menuItemText: this.translateService.instant(
            'SETTINGS.FONT_SIZE_TITLE'
          ),
          menuItemLabel: this.translateService.instant('FONT_SIZE_LABEL'),
          menuItemKey: 'FONT_SIZE',
          menuItemType: 'switch',
          menuItemOptions: [
            { label: 'ON', value: true },
            { label: 'OFF', value: false },
          ],
          selectedMenuItemOption: settings?.fontSize === '20px',
        },
        {
          menuItemText: this.translateService.instant(
            'SETTINGS.TRANSCRIPT_TITLE'
          ),
          menuItemLabel: this.translateService.instant('TRANSCRIPT_LABEL'),
          menuItemKey: 'TRANSCRIPT',
          menuItemType: 'link',
          isMenuItemVisible: appConfig.isChatTranscriptEnabled,
        },
      ];
    })
  );
  isUserMessageDisabled$ = this.store.select(selectUserInputState);
  isVaOrAgentTyping$ = this.store.select(selectIsVaOrAgentTyping);
  headerLogo$ = this.store.select(selectLogo);
  headerTitle$ = this.store.select(selectTitle);
  isLiveChatHeader$ = this.store.select(selectIsLiveChatHeader);
  addressSuggestions$ = this.store.select(selectAddressList);

  constructor(
    private componentFactoryService: ComponentFactoryService,
    private actions$: Actions,
    private translateService: TranslateService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store.dispatch(MainComponentActions.mainComponentLoaded());
  }

  ngAfterViewInit(): void {
    this.subscribeToNewMessages();
    this.subscribeToMessagesState();
    this.subscribeToDeleteMessage();
    this.subscribeToUpdateMessage();
    this.subscribeToCloseWindow();
    this.subscribeToOverlayState();
    this.subscribeToAlertBanner();
  }

  subscribeToNewMessages(): void {
    this.actions$
      .pipe(ofType(MessagesActions.addNewMessages))
      .subscribe((action) => {
        this.displayMessages(action.messages);
      });
  }

  subscribeToMessagesState(): void {
    this.store
      .select(selectMessagesState)
      .pipe(take(1))
      .subscribe((messages) => {
        if (messages?.length > 0) {
          this.displayMessages(messages);
        }
      });
  }

  subscribeToDeleteMessage(): void {
    this.actions$
      .pipe(ofType(MessagesActions.removeLastNMessages))
      .subscribe((action) => {
        this.componentFactoryService.deleteComponents(
          this.messageContainerRef,
          action.numberOfItems
        );
      });
  }

  subscribeToUpdateMessage(): void {
    this.actions$
      .pipe(
        ofType(
          MessagesActions.updateLastMessage,
          MessagesActions.updateQuickReply
        )
      )
      .subscribe((action) => {
        this.componentFactoryService.deleteComponents(
          this.messageContainerRef,
          1
        );
        this.componentFactoryService.loadMessageComponents(
          this.messageContainerRef,
          [action.updatedMessage]
        );
      });
  }

  subscribeToCloseWindow(): void {
    this.actions$
      .pipe(
        ofType(WindowStateActions.closeWindow, AuthTokenActions.startSession)
      )
      .subscribe(() => {
        this.messageContainerRef?.clear();
        this.overlayComponentRef?.clear();
        if (this.footerContainer && this.footerContainer['textAreaElement']) {
          this.footerContainer['textAreaElement'].nativeElement.value = '';
        }
      });
  }

  subscribeToOverlayState(): void {
    this.store
      .select(selectOverlayState)
      .subscribe((state: Readonly<OverlayState>) => {
        if (state.isVisible) {
          this.componentFactoryService.loadOverlayComponent(
            this.overlayComponentRef,
            state.type
          );
        } else {
          this.overlayComponentRef.clear();
        }
      });
  }

  subscribeToAlertBanner() {
    this.actions$
      .pipe(ofType(ChatAdminActions.loadAlertBanner))
      .subscribe((value) => {
        this.componentFactoryService.loadAlertBanner(
          value.alertConfig,
          this.alertBannerRef
        );
      });
  }

  displayMessages(messages: Readonly<Message[]>) {
    this.componentFactoryService.loadMessageComponents(
      this.messageContainerRef,
      messages
    );
    if (messages.length) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  scrollToBottom(): void {
    if (this.messageContainer) {
      this.messageContainer.nativeElement.scrollTop =
        this.messageContainer.nativeElement.scrollHeight;
    }
  }

  onMenuItemChanged = (event) => {
    const value = event.value;
    switch (event.key) {
      case 'LANGUAGE':
        this.store.dispatch(SettingsActions.setLanguage({ value }));
        break;
      case 'NOTIFICATIONS':
        this.store.dispatch(SettingsActions.setNotifications({ value }));
        break;
      case 'FONT_SIZE':
        this.store.dispatch(
          SettingsActions.setFontSize({
            value: getFontSize({ isLargeFont: value }),
          })
        );
        break;
      case 'TRANSCRIPT':
        this.store.dispatch(SettingsActions.setTranscript({ value }));
        break;
    }
  };

  onMinimizeChat = () => {
    this.store.dispatch(WindowStateActions.requestToMinimizeWindow());
  };

  onCloseChat = () => {
    this.store.dispatch(
      OverlayActions.showOverlay({ value: OverlayType.Confirmation })
    );
  };

  get version(): string {
    return appConfig.appVersion;
  }

  onSendUserMessage = (message: string): void => {
    if (message) {
      this.store.dispatch(
        MessagesActions.handleUserMessage({ query: message })
      );
      this.store.dispatch(AddressAutocompleteAction.clearAddressList());
    }
  };

  onAddressSearch = (address: string): void => {
    if (address) {
      this.store.dispatch(
        AddressAutocompleteAction.setKeyword({ value: address })
      );
    }
  };

  onClearAddresses = (): void => {
    this.store.dispatch(AddressAutocompleteAction.clearAddressList());
  };

  onUserTyping = (isTyping: boolean): void => {
    this.store.dispatch(TypingActions.markUserTyping({ value: isTyping }));
  };

  ngOnDestroy(): void {
    this.store.dispatch(MainComponentActions.mainComponentDestroyed());
  }

  getBgColor(): string {
    if (getBrand().name == RealmName.shaw) {
      return '#000000';
    }
    return '';
  }
}
